import React, { useState, useEffect } from 'react';
import { X, FileText, Save, Lock, Globe } from 'lucide-react';
import { cn } from '../utils/cn';

interface SaveAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string; tags?: string; isPublic: boolean }) => void;
  isLoading?: boolean;
  pageTitle: string;
  isAdmin?: boolean;
}

export const SaveAsTemplateModal: React.FC<SaveAsTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  pageTitle,
  isAdmin = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName(`${pageTitle} Template`);
      setDescription(`Template based on the "${pageTitle}" page`);
      setTags('');
      setIsPublic(false);
      setErrors({});
    }
  }, [isOpen, pageTitle]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      tags: tags.trim() || undefined,
      isPublic,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={cn(
        "modal-backdrop",
        "flex items-center justify-center z-50 p-4",
        isOpen ? "animate-fade-in" : ""
      )}
      onClick={handleBackdropClick}
    >
      <div className={cn(
        "modal-content",
        "max-w-lg w-full max-h-screen overflow-y-auto",
        isOpen ? "animate-slide-up" : ""
      )}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FileText className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Save as Template</h2>
              <p className="text-sm text-gray-500 mt-1">Create a reusable template from this page</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              "p-2 text-gray-400 hover:text-gray-600 rounded-lg",
              "hover:bg-gray-100 transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            )}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-5">
            {/* Template Name */}
            <div>
              <label htmlFor="templateName" className="block text-sm font-semibold text-gray-700 mb-2">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                id="templateName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cn(
                  'input-field',
                  errors.name && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                )}
                placeholder="e.g., Landing Page Template, About Us Template"
                disabled={isLoading}
                autoFocus
              />
              {errors.name && (
                <p className="form-error">
                  <span>{errors.name}</span>
                </p>
              )}
            </div>

            {/* Template Description */}
            <div>
              <label htmlFor="templateDescription" className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                id="templateDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input-field"
                placeholder="Describe what this template is for and when to use it..."
                rows={3}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Help others understand when to use this template
              </p>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="templateTags" className="block text-sm font-semibold text-gray-700 mb-2">
                Tags
              </label>
              <input
                id="templateTags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="input-field"
                placeholder="e.g., landing, business, portfolio"
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated tags to help categorize and find this template
              </p>
            </div>

            {/* Visibility - Radio Buttons (only show for admins) */}
            {isAdmin && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Visibility
                </label>
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      checked={!isPublic}
                      onChange={() => setIsPublic(false)}
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                      disabled={isLoading}
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Lock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-900">Private</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Only you can see and use this template
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="visibility"
                      checked={isPublic}
                      onChange={() => setIsPublic(true)}
                      className="mt-0.5 text-primary-600 focus:ring-primary-500"
                      disabled={isLoading}
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-900">Public</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Other users can discover and use this template
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn(
                'btn-primary flex items-center space-x-2',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Save Template</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 